// FooterDoodle.jsx
//
// A small hand-drawn doodle that lives above the footer copyright.
// Every time the footer scrolls back into view, it picks a new random
// landmark from a small set and animates it on — same draw-on technique
// as the Albany skyline (stroke-dasharray + dashoffset), but each
// doodle is self-contained in an 80×80 viewBox.
//
// Each landmark also carries a short LABEL that types itself out (a
// typewriter caption) beneath the drawing, so the silhouette is never
// ambiguous — the caption literally names "what it is supposed to be"
// (e.g. The Egg, Nott Memorial). This turns the doodle from a faint
// unreadable sketch into a tiny captioned field-note of a 518 place.
//
//   - "egg"                — The Egg (Albany's most iconic shape)
//   - "corning"            — Corning Tower (the Capital District's tallest)
//   - "capitol"            — NYS Capitol w/ mansard + corner turrets
//   - "egg-corning"        — The Egg + Corning = Empire State Plaza
//   - "smokestack-corning" — Troy smokestack + Corning (industrial 518)
//   - "full-mini"          — all four silhouettes (the 518 skyline)
//   - "nott"               — Nott Memorial dome (Schenectady)
//   - "troy-row"           — a Troy brownstone rowhouse run
//   - "egg-capitol"        — The Egg + the Capitol (Albany)
//   - "towers"             — the three Empire State Plaza agency towers
//
// We rotate by random pick (avoiding immediate repeat) rather than a
// fixed sequence so it feels like an easter egg, not a counter. Each
// re-entry into the viewport triggers a fresh draw — but only when the
// element transitions from "out of view" → "in view", so brief scroll
// jitters around the boundary don't fire it.

import React, { useEffect, useRef, useState } from 'react';
import '../styles/FooterDoodle.css';

const DOODLES = [
  {
    id: 'egg',
    label: 'The Egg',
    paths: [
      // The Egg — centered. Saucer (two arcs) + tapered pedestal + plaza base.
      'M 14 42 Q 40 26 66 42',
      'M 14 42 Q 40 50 66 42',
      'M 30 46 L 30 66',
      'M 50 46 L 50 66',
      'M 22 68 L 58 68',
    ],
  },
  {
    id: 'corning',
    label: 'Corning Tower',
    paths: [
      // Corning Tower — tall modernist rectangle + slim crown.
      'M 32 68 L 32 16 L 52 16 L 52 68',
      'M 36 16 L 36 8 L 48 8 L 48 16',
    ],
  },
  {
    id: 'capitol',
    label: 'NYS Capitol',
    paths: [
      // NYS Capitol — main block + mansard + two corner turrets w/ caps.
      'M 8 64 L 8 40 L 72 40 L 72 64',
      'M 8 40 L 18 30 L 62 30 L 72 40',
      'M 14 40 L 14 22 L 22 22 L 22 40',
      'M 14 22 L 18 16 L 22 22',
      'M 58 40 L 58 22 L 66 22 L 66 40',
      'M 58 22 L 62 16 L 66 22',
    ],
  },
  {
    id: 'egg-corning',
    label: 'Empire State Plaza',
    paths: [
      // The Egg (left, smaller) + Corning Tower (right, taller). The
      // pair captures the Empire State Plaza in two strokes-of-language.
      'M 6 52 Q 22 38 38 52',
      'M 6 52 Q 22 58 38 52',
      'M 16 55 L 16 68',
      'M 28 55 L 28 68',
      'M 52 68 L 52 18 L 70 18 L 70 68',
      'M 55 18 L 55 10 L 67 10 L 67 18',
    ],
  },
  {
    id: 'smokestack-corning',
    label: 'Troy & the Plaza',
    paths: [
      // Troy industrial smokestack (left) + Corning Tower (right) —
      // wider-518 industrial heritage paired with the modernist Plaza.
      'M 14 68 L 14 22 L 22 22 L 22 68',
      'M 12 26 L 24 26',
      'M 48 68 L 48 12 L 66 12 L 66 68',
      'M 51 12 L 51 6 L 63 6 L 63 12',
    ],
  },
  {
    id: 'full-mini',
    label: 'the 518 skyline',
    paths: [
      // All four silhouettes in miniature, left → right. The "thumbnail"
      // of the About-hero skyline.
      'M 6 68 L 6 34 L 10 34 L 10 68',
      'M 18 68 L 18 50 L 42 50 L 42 68',
      'M 18 50 L 22 44 L 38 44 L 42 50',
      'M 44 56 Q 52 48 60 56',
      'M 44 56 Q 52 60 60 56',
      'M 50 58 L 50 68',
      'M 66 68 L 66 24 L 74 24 L 74 68',
    ],
  },
  {
    id: 'nott',
    label: 'Nott Memorial',
    paths: [
      // Nott Memorial (Schenectady / Union College) — a domed 16-sided
      // rotunda: drum + dome + finial, two column hints, plaza base.
      'M 28 66 L 28 42 L 52 42 L 52 66',
      'M 25 42 Q 40 20 55 42',
      'M 40 20 L 40 13',
      'M 36 66 L 36 46',
      'M 44 66 L 44 46',
      'M 22 66 L 58 66',
    ],
  },
  {
    id: 'troy-row',
    label: 'Troy rowhouses',
    paths: [
      // A run of Troy brownstone rowhouses — three stepped façades on a
      // shared cornice/sidewalk, with a stoop at the left end.
      'M 12 66 L 12 38 L 28 38 L 28 66',
      'M 28 66 L 28 34 L 44 34 L 44 66',
      'M 44 66 L 44 40 L 60 40 L 60 66',
      'M 8 66 L 64 66',
      'M 8 66 L 12 61',
    ],
  },
  {
    id: 'egg-capitol',
    label: 'Albany, NY',
    paths: [
      // The Egg (left) paired with the NYS Capitol (right) — the two
      // halves of the Albany civic skyline.
      'M 6 50 Q 17 41 28 50',
      'M 6 50 Q 17 55 28 50',
      'M 13 52 L 13 64',
      'M 21 52 L 21 64',
      'M 38 64 L 38 46 L 70 46 L 70 64',
      'M 38 46 L 45 40 L 63 40 L 70 46',
      'M 42 46 L 42 35 L 48 35 L 48 46',
      'M 42 35 L 45 31 L 48 35',
    ],
  },
  {
    id: 'towers',
    label: 'the Plaza towers',
    paths: [
      // The three Empire State Plaza agency towers, stepped heights.
      'M 14 66 L 14 28 L 24 28 L 24 66',
      'M 30 66 L 30 18 L 40 18 L 40 66',
      'M 46 66 L 46 32 L 56 32 L 56 66',
      'M 10 66 L 60 66',
    ],
  },
];

// Field-log metadata per landmark: a (made-up-but-stable) catalog number, the
// 518 town it sits in, and an approximate latitude. Rendered as a tiny hand-noted
// "specimen" caption — `no. 037 · the egg · albany, ny · 42.65° n` — so reaching
// the bottom feels like collecting one more entry in a field guide to the
// Capital Region. Scattered numbers imply a much larger catalog. (Idea from the
// blind creative panel; two creatives independently invented it.)
const META = {
  egg:                  { no: 37,  town: 'Albany, NY',      coord: '42.65° N' },
  corning:              { no: 52,  town: 'Albany, NY',      coord: '42.65° N' },
  capitol:              { no: 8,   town: 'Albany, NY',      coord: '42.66° N' },
  'egg-corning':        { no: 41,  town: 'Albany, NY',      coord: '42.65° N' },
  'smokestack-corning': { no: 63,  town: 'Troy, NY',        coord: '42.73° N' },
  'full-mini':          { no: 100, town: 'the 518',         coord: '42.7° N'  },
  nott:                 { no: 74,  town: 'Schenectady, NY', coord: '42.81° N' },
  'troy-row':           { no: 59,  town: 'Troy, NY',        coord: '42.73° N' },
  'egg-capitol':        { no: 12,  town: 'Albany, NY',      coord: '42.65° N' },
  towers:               { no: 45,  town: 'Albany, NY',      coord: '42.65° N' },
};

function pickNext(prev) {
  if (DOODLES.length <= 1) return 0;
  let next;
  do {
    next = Math.floor(Math.random() * DOODLES.length);
  } while (next === prev);
  return next;
}

const FooterDoodle = () => {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  // Pick the initial doodle on mount so first render isn't always the
  // same id (which would feel canned).
  const [idx, setIdx] = useState(() => Math.floor(Math.random() * DOODLES.length));
  // `drawKey` bumps on every re-entry so the SVG remounts and the
  // animation replays from frame 0 (CSS animations don't naturally
  // restart on identical-element re-render).
  const [drawKey, setDrawKey] = useState(0);
  // The label text revealed so far by the typewriter caption.
  const [typed, setTyped] = useState('');

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    // Track whether the element is currently out of view; only fire on
    // the out → in transition so jitter at the boundary doesn't loop.
    let wasOutOfView = true;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (wasOutOfView) {
              setIdx((prev) => pickNext(prev));
              setDrawKey((k) => k + 1);
            }
            wasOutOfView = false;
          } else {
            wasOutOfView = true;
          }
        });
      },
      // Modest threshold — fire when ~30% of the doodle is in the
      // viewport. Higher would feel sluggish; lower triggers off-screen.
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Measure path lengths whenever the active doodle changes so the
  // draw-on animation reveals each path over its actual length.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const paths = svg.querySelectorAll('path');
    paths.forEach((path) => {
      // jsdom has no SVG geometry impl; skip measurement so unit tests
      // don't crash. The CSS fallback --draw-length still renders.
      if (typeof path.getTotalLength !== 'function') return;
      const len = path.getTotalLength() + 1;
      path.style.setProperty('--draw-length', `${len}`);
    });
    svg.dataset.drawn = 'true';
  }, [drawKey]);

  const doodle = DOODLES[idx];
  const label = doodle.label;
  const meta = META[doodle.id] || { no: 0, town: 'the 518', coord: '42.7° N' };
  const catalogNo = String(meta.no).padStart(3, '0');

  // Typewriter caption — retype the active landmark's name each time it
  // (re)draws. Reduced-motion users get the full label at once (no typing).
  useEffect(() => {
    const reduce =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setTyped(label);
      return undefined;
    }
    setTyped('');
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setTyped(label.slice(0, i));
      if (i >= label.length) clearInterval(id);
    }, 65);
    return () => clearInterval(id);
  }, [idx, drawKey, label]);

  return (
    <div ref={containerRef} className="footer-doodle" aria-hidden="true">
      <svg
        key={drawKey}
        ref={svgRef}
        viewBox="0 0 80 80"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        data-doodle={doodle.id}>
        {doodle.paths.map((d, i) => (
          <path key={i} d={d} style={{ '--i': i }} />
        ))}
      </svg>
      {/* Field-log caption: a tiny catalogued-specimen line. The landmark name
          still types itself in; the catalog no., town, and coordinate frame it
          like a hand-noted field-guide entry. */}
      <div className="footer-doodle__label">
        <span className="footer-doodle__log">no. {catalogNo} ·&nbsp;</span>
        <span className="footer-doodle__label-text">{typed}</span>
        <span className="footer-doodle__caret" />
        <span className="footer-doodle__log">&nbsp;· {meta.town} · {meta.coord}</span>
      </div>
    </div>
  );
};

export default FooterDoodle;
